import { useEffect, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { http, notify, money, documentTypes } from "../api";
import { Modal, Field } from "./common";

/* ---------------- DUPLICATE WARNING (used by LeadForm) ---------------- */
function DuplicateWarning({ mobile, pan, excludeId }) {
  const [hits, setHits] = useState([]);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const latest = useRef(0);

  const cleanMobile = (mobile || "").replace(/\D/g, "");
  const cleanPan = (pan || "").trim().toUpperCase();
  const key = `${cleanMobile}|${cleanPan}`;

  useEffect(() => {
    setDismissed(false);
    if (cleanMobile.length < 6 && cleanPan.length < 5) {
      setHits([]);
      return;
    }
    const id = ++latest.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const params = new URLSearchParams();
        if (cleanMobile.length >= 6) params.set("mobile", cleanMobile);
        if (cleanPan.length >= 5) params.set("pan", cleanPan);
        if (excludeId) params.set("exclude", excludeId);
        const r = await http.get(`/leads/check-duplicate?${params.toString()}`);
        if (id === latest.current) setHits(r.data.duplicates || []);
      } catch {
        // silent — duplicate check is best-effort
      } finally {
        if (id === latest.current) setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [key, excludeId]);

  if (dismissed || !hits.length) return null;

  return (
    <div className="duplicate-warning" data-testid="duplicate-warning" role="alert">
      <div className="duplicate-warning-head">
        <AlertTriangle size={17} />
        <div>
          <strong>
            {hits.length === 1
              ? "This customer already exists on another lead"
              : `${hits.length} existing leads match this contact`}
          </strong>
          <small>
            You can still create the lead, but check with the current owner first
            to avoid duplicate outreach.
          </small>
        </div>
        <button
          type="button"
          className="link-button"
          onClick={() => setDismissed(true)}
          data-testid="duplicate-warning-dismiss"
          aria-label="Dismiss warning"
        >
          Dismiss
        </button>
      </div>
      <ul>
        {hits.map((h) => (
          <li key={h.lead_id} data-testid={`duplicate-hit-${h.lead_id}`}>
            <span className="dup-id mono">{h.lead_id}</span>
            <span className="dup-name">
              {h.customer_name}
              {!h.in_your_scope && <span className="dup-masked-badge">outside your scope</span>}
            </span>
            <span className="dup-meta">
              {h.match_field.includes("mobile") ? "mobile" : ""}
              {h.match_field.includes("mobile") && h.match_field.includes("pan") ? " + " : ""}
              {h.match_field.includes("pan") ? "PAN" : ""}
              {" match"}
            </span>
            <span className="dup-owner">
              {h.assigned_name || "Owner?"} · {h.sales_team_name || "team unknown"}
              {h.sales_manager_name ? ` · Mgr ${h.sales_manager_name}` : ""}
            </span>
            <span className={`dup-status status ${(h.status || "").toLowerCase().replaceAll(" ", "-")}`}>
              {h.status}
            </span>
          </li>
        ))}
      </ul>
      {loading && <small className="dup-loading">checking again…</small>}
    </div>
  );
}

/* ---------------- LEAD ---------------- */
// Loan-application type dropdown for the Create Lead form (Fresh, BT, Top-Up etc.).
const LEAD_LOAN_TYPES = ["Fresh", "BT", "Top Up", "OD", "CC", "Express Loan"];
const ADDRESS_TYPES   = ["Rented", "Permanent", "Company Provided", "NRI"];

export function LeadForm({ lead, prefill, onClose, onSaved, user }) {
  const empty = {
    customer_name: "", mobile: "", email: "", pan: "",
    address: "", address_type: "",
    city: "", state: "", pincode: "",
    employment_type: "Salaried", company: "", designation: "",
    monthly_income: 0, existing_pos: 0, existing_emi: 0,
    loan_amount: 0, tenure_months: "",
    loan_type: "Fresh", bank_id: "", bank_name: "",
    product_id: "", product_name: "", loan_purpose: "",
    other_information: "", campaign_id: "",
    roi: "", status: "New",
  };

  // ---- Draft persistence + idempotency (new-lead mode only) ----
  // A stable UUID per form-open, resurrected from localStorage on refresh so
  // the backend dedupes an accidental resubmit.
  const isNewMode = !lead;
  // Namespace the draft by prefill scope so a prefilled Call → Create Lead
  // form never clobbers a plain Create Lead draft, and so different SAs on
  // the same browser stay isolated.
  const draftKey = isNewMode
    ? `lead-draft::${user?.id || "anon"}::${prefill?.customer_id || "new"}`
    : null;

  const readDraft = () => {
    if (!draftKey) return null;
    try {
      const raw = localStorage.getItem(draftKey);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  };
  const restored = isNewMode ? readDraft() : null;

  const clientReqIdRef = useRef(
    restored?.crid
      || (typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `crid-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`),
  );

  const [form, setForm] = useState(
    lead ? { ...empty, ...lead }
         : restored?.form ? { ...empty, ...restored.form }
         : prefill ? { ...empty, ...prefill } : empty,
  );
  const [banks, setBanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  // Company auto-fill: resolve typed employer → company_id from the master,
  // then call /companies/lookup(company_id, bank_id, product_id) to reveal
  // the lender-specific A-Z category chip on the form.
  const [companyId, setCompanyId] = useState(null);
  const [companySuggestions, setCompanySuggestions] = useState([]);
  const [lenderCategory, setLenderCategory] = useState(null); // {category, ...} | null
  // Editing an existing lead: only admins can change every field. Back office
  // Back Office roles are restricted to Bank/product/loan amount only
  // (server enforces too). Admins + SA review-owners see the full form —
  // the SA needs it to correct customer_name, city, loan_purpose etc.
  // before clicking Send to Login.
  const isBackOffice = user?.role === "back_office_manager"
    || user?.role === "back_office_executive";
  const isSaReviewOwner = !!lead
    && user?.role === "telecaller"
    && lead.assigned_to === user?.id
    && lead.review_status === "pending_review";
  const restricted = !!lead && isBackOffice && !isSaReviewOwner;
  const requiresReason = !!lead;

  useEffect(() => {
    http.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (form.bank_id) {
      http.get(`/products?bank_id=${form.bank_id}`).then((r) => setProducts(r.data)).catch(() => {});
    } else setProducts([]);
  }, [form.bank_id]);

  // Company typeahead — resolve name → company_id from master.
  useEffect(() => {
    const q = (form.company || "").trim();
    if (!q) {
      setCompanySuggestions([]); setCompanyId(null); setLenderCategory(null);
      return;
    }
    const t = setTimeout(() => {
      http.get(`/companies/search?q=${encodeURIComponent(q)}&limit=6`)
        .then((r) => {
          const list = r.data.companies || [];
          setCompanySuggestions(list);
          const hit = list.find((c) => c.name.toLowerCase() === q.toLowerCase());
          setCompanyId(hit ? hit.company_id : null);
        })
        .catch(() => setCompanySuggestions([]));
    }, 220);
    return () => clearTimeout(t);
  }, [form.company]);

  // Category lookup fires whenever (company, bank, product) triple is set.
  useEffect(() => {
    if (!companyId || !form.bank_id || !form.product_id) {
      setLenderCategory(null); return;
    }
    http.get(`/companies/lookup?company_id=${companyId}&bank_id=${form.bank_id}&product_id=${form.product_id}`)
      .then((r) => setLenderCategory(r.data))
      .catch(() => setLenderCategory({ category: null, no_mapping: true }));
  }, [companyId, form.bank_id, form.product_id]);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  // Persist a lightweight snapshot to localStorage on every change so an
  // accidental refresh / redirect / crash never loses what the SA typed.
  // Only in new-lead mode — never for edits.
  useEffect(() => {
    if (!draftKey) return;
    // Skip persistence if the form is still literally empty (avoids
    // stamping a blank draft the moment the modal opens).
    const hasAnything = Object.entries(form).some(
      ([k, v]) => k !== "employment_type" && k !== "loan_type" && k !== "status"
                  && v !== "" && v !== 0 && v !== null && v !== undefined,
    );
    if (!hasAnything) return;
    try {
      localStorage.setItem(draftKey, JSON.stringify({
        form, crid: clientReqIdRef.current, ts: Date.now(),
      }));
    } catch { /* quota / SSR — ignore */ }
  }, [form, draftKey]);

  // One-time friendly hint when we restored a draft.
  useEffect(() => {
    if (restored?.form) {
      notify.info("Restored your unfinished lead — continue and click Create to save.");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return; // hard guard against double-submit
    if (requiresReason && !reason.trim()) {
      notify.error("A reason for the change is required for the audit trail.");
      return;
    }
    setBusy(true);
    try {
      if (lead) {
        // Editing: only send the fields this role is allowed to change.
        const payload = restricted
          ? {
              bank_id: form.bank_id, bank_name: form.bank_name,
              product_id: form.product_id, product_name: form.product_name,
              loan_amount: Number(form.loan_amount) || 0,
              reason: reason.trim(),
            }
          : {
              ...form,
              monthly_income: Number(form.monthly_income) || 0,
              loan_amount: Number(form.loan_amount) || 0,
              existing_emi: Number(form.existing_emi) || 0,
              roi: form.roi === "" || form.roi === null ? null : Number(form.roi),
              tenure_months: form.tenure_months === "" || form.tenure_months === null ? null : Number(form.tenure_months),
              reason: reason.trim(),
            };
        await http.patch(`/leads/${lead.lead_id}`, payload);
        notify.success("Lead updated");
        onSaved(lead);
      } else {
        const payload = {
          ...form,
          monthly_income: Number(form.monthly_income) || 0,
          loan_amount: Number(form.loan_amount) || 0,
          existing_emi: Number(form.existing_emi) || 0,
          existing_pos: Number(form.existing_pos) || 0,
          roi: form.roi === "" || form.roi === null ? null : Number(form.roi),
          tenure_months: form.tenure_months === "" || form.tenure_months === null ? null : Number(form.tenure_months),
          client_request_id: clientReqIdRef.current,
        };
        const r = await http.post("/leads", payload);
        // Draft committed — discard the localStorage copy so the next
        // Create Lead starts fresh.
        if (draftKey) { try { localStorage.removeItem(draftKey); } catch {} }
        notify.success("Lead created");
        onSaved(r.data);
      }
    } catch (err) {
      notify.error(err, "Could not save lead");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={lead ? "Edit customer lead" : "Create a new lead"} onClose={onClose} wide>
      <form className="record-form" onSubmit={submit}>
        {restricted && (
          <p className="upload-note" data-testid="lead-form-restricted-note">
            You can update Bank/NBFC, Loan product, and Loan amount only. Everything else is locked once the lead is created.
          </p>
        )}
        {!restricted && (
          <DuplicateWarning
            mobile={form.mobile}
            pan={form.pan}
            excludeId={lead?.lead_id}
          />
        )}
        <div className="form-grid form-grid-strict-2col">
          {!restricted && <Field name="customer_name" label="Customer Name" value={form.customer_name} onChange={change} required />}
          {!restricted && <Field name="mobile" label={isSaReviewOwner ? "Mobile (read-only)" : "Mobile"} value={form.mobile} onChange={change} required readOnly={isSaReviewOwner} />}
          {!restricted && <Field name="email" label="Email" value={form.email} onChange={change} type="email" />}
          {!restricted && <Field name="pan" label="PAN" value={form.pan} onChange={change} />}
          {!restricted && <Field name="address" label="Address" value={form.address} onChange={change} />}
          {!restricted && (
            <label>
              Address Type
              <select name="address_type" value={form.address_type} onChange={change}>
                <option value="">Select</option>
                {ADDRESS_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
          )}
          {!restricted && <Field name="city" label="City" value={form.city} onChange={change} />}
          {!restricted && <Field name="state" label="State" value={form.state} onChange={change} />}
          {!restricted && <Field name="pincode" label="Pincode" value={form.pincode} onChange={change} />}
          {!restricted && (
            <label>
              Employment
              <select name="employment_type" value={form.employment_type} onChange={change}>
                <option>Salaried</option>
                <option>Self Employed</option>
                <option>Business</option>
                <option>Professional</option>
              </select>
            </label>
          )}
          {!restricted && <Field name="designation" label="Designation" value={form.designation} onChange={change} />}
          {!restricted && <Field name="monthly_income" label="Income" value={form.monthly_income} onChange={change} type="number" />}
          {!restricted && <Field name="existing_pos" label="Existing POS" value={form.existing_pos} onChange={change} type="number" />}
          {!restricted && <Field name="existing_emi" label="Existing EMI" value={form.existing_emi} onChange={change} type="number" />}
          <Field name="loan_amount" label="Loan Amount" value={form.loan_amount} onChange={change} type="number" />
          {!restricted && <Field name="tenure_months" label="Tenure (months)" value={form.tenure_months ?? ""} onChange={change} type="number" />}
          {!restricted && (
            <label>
              Loan Type
              <select name="loan_type" value={form.loan_type} onChange={change}>
                {LEAD_LOAN_TYPES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </label>
          )}
          <label className={form.bank_id ? "" : "field-required-empty"}>
            Preferred Bank / NBFC {!form.bank_id && <span className="req-star" title="Pick manually before saving">*</span>}
            <select
              data-testid="lead-form-bank-select"
              name="bank_id"
              value={form.bank_id}
              onChange={(e) => {
                const b = banks.find((x) => x.bank_id === e.target.value);
                setForm({ ...form, bank_id: e.target.value, bank_name: b?.name || "", product_id: "", product_name: "" });
              }}
            >
              <option value="">Not decided</option>
              {banks.map((b) => (
                <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label className={form.product_id ? "" : "field-required-empty"}>
            Loan Product {!form.product_id && <span className="req-star" title="Pick manually before saving">*</span>}
            <select
              data-testid="lead-form-product-select"
              name="product_id"
              value={form.product_id}
              onChange={(e) => {
                const p = products.find((x) => x.product_id === e.target.value);
                setForm({ ...form, product_id: e.target.value, product_name: p?.name || "" });
              }}
              disabled={!form.bank_id}
            >
              <option value="">Select product</option>
              {products.map((p) => (
                <option key={p.product_id} value={p.product_id}>
                  {`${p.name} (${p.loan_type})`}
                </option>
              ))}
            </select>
          </label>
          {!restricted && (
            <label>
              Loan Purpose
              <input
                data-testid="lead-form-loan_purpose-input"
                name="loan_purpose"
                value={form.loan_purpose || ""}
                onChange={change}
              />
            </label>
          )}
          {!restricted && (
            <label>
              Other Information
              <input
                data-testid="lead-form-other-info-input"
                name="other_information"
                value={form.other_information || ""}
                onChange={change}
                placeholder="Notes, referrer, cibil…"
              />
            </label>
          )}
          {form.campaign_id && (
            <label data-testid="lead-form-campaign-id">
              Campaign ID
              <input
                readOnly
                value={form.campaign_id}
                style={{ fontFamily: "monospace", background: "var(--alt)", cursor: "not-allowed" }}
              />
            </label>
          )}
          {/* Company field spans full width so the typeahead suggestions
              have room without breaking the 2-col rhythm. */}
          {!restricted && (
            <label className="span-2" style={{ position: "relative" }}>
              Company / Business
              <input
                data-testid="lead-form-company-input"
                name="company"
                value={form.company || ""}
                onChange={change}
                autoComplete="off"
              />
              {companySuggestions.length > 0 && !companyId && form.company && (
                <ul
                  data-testid="lead-form-company-suggestions"
                  style={{
                    position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20,
                    background: "var(--surface)", border: "1px solid var(--border)",
                    listStyle: "none", padding: 0, margin: "2px 0 0", maxHeight: 220, overflow: "auto",
                  }}
                >
                  {companySuggestions.map((c) => (
                    <li key={c.company_id}>
                      <button
                        type="button"
                        onClick={() => {
                          setForm({ ...form, company: c.name });
                          setCompanyId(c.company_id);
                          setCompanySuggestions([]);
                        }}
                        style={{ width: "100%", textAlign: "left", padding: "8px 12px", background: "transparent", border: 0, cursor: "pointer", borderTop: "1px solid var(--border)" }}
                      >
                        {c.name}
                        <small className="muted" style={{ marginLeft: 8 }}>
                          {(c.mappings || []).length} mapping{(c.mappings || []).length === 1 ? "" : "s"}
                        </small>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {lenderCategory && lenderCategory.category && (
                <span
                  className="chip chip-green"
                  data-testid="lead-form-category-chip"
                  style={{ marginTop: 6, alignSelf: "flex-start", fontSize: 11 }}
                >
                  Category <strong>{lenderCategory.category}</strong> · {lenderCategory.bank_name} · {lenderCategory.product_name}
                </span>
              )}
              {lenderCategory && lenderCategory.no_mapping && companyId && form.bank_id && form.product_id && (
                <span
                  className="chip chip-amber"
                  data-testid="lead-form-category-none"
                  style={{ marginTop: 6, alignSelf: "flex-start", fontSize: 11 }}
                >
                  No mapping for this lender · ask Admin to add it
                </span>
              )}
            </label>
          )}
        </div>
        {requiresReason && (
          <label>
            Reason for change <span style={{ color: "var(--red)" }}>*</span>
            <textarea
              data-testid="lead-form-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="Recorded on the audit trail (e.g. 'Customer requested higher tenure — moved to ICICI')"
              required
            />
          </label>
        )}
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button data-testid="lead-form-submit-button" className="primary-button" type="submit" disabled={busy}>
            {busy ? "Saving…" : lead ? "Save changes" : "Create lead"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- APPLICATION ---------------- */
export function ApplicationForm({ leadId, defaultAmount = 0, onClose, onSaved }) {
  const [form, setForm] = useState({
    bank_id: "", bank_name: "", product_id: "", product_name: "", loan_type: "",
    requested_amount: defaultAmount, application_number: "", status: "Processing",
    commission_percent: "", commission_amount: "",
    roi: "", tenure_months: "",
  });
  const [banks, setBanks] = useState([]);
  const [products, setProducts] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    http.get("/banks").then((r) => setBanks(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (form.bank_id) {
      http.get(`/products?bank_id=${form.bank_id}`).then((r) => setProducts(r.data)).catch(() => {});
    } else setProducts([]);
  }, [form.bank_id]);

  const previewCommission = form.commission_percent && form.requested_amount
    ? (Number(form.requested_amount) * Number(form.commission_percent)) / 100
    : Number(form.commission_amount || 0);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.bank_id || !form.product_id) {
      notify.error("Select a partner bank and product");
      return;
    }
    setBusy(true);
    try {
      await http.post("/applications", {
        ...form, lead_id: leadId,
        requested_amount: Number(form.requested_amount) || 0,
        commission_percent: Number(form.commission_percent) || 0,
        commission_amount: Number(form.commission_amount) || 0,
        roi: form.roi === "" || form.roi === null ? null : Number(form.roi),
        tenure_months: form.tenure_months === "" || form.tenure_months === null ? null : Number(form.tenure_months),
      });
      notify.success("Application created");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not create application");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add loan application" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <div className="form-grid">
          <label>
            Partner bank / NBFC
            <select
              data-testid="application-bank-select"
              value={form.bank_id}
              onChange={(e) => {
                const b = banks.find((x) => x.bank_id === e.target.value);
                setForm({
                  ...form,
                  bank_id: e.target.value,
                  bank_name: b?.name || "",
                  product_id: "",
                  product_name: "",
                  commission_percent: b?.default_commission_percent || form.commission_percent,
                });
              }}
              required
            >
              <option value="">Select bank</option>
              {banks.map((b) => (
                <option key={b.bank_id} value={b.bank_id}>{b.name}</option>
              ))}
            </select>
          </label>
          <label>
            Product
            <select
              data-testid="application-product-select"
              value={form.product_id}
              onChange={(e) => {
                const p = products.find((x) => x.product_id === e.target.value);
                setForm({ ...form, product_id: e.target.value, product_name: p?.name || "", loan_type: p?.loan_type || "" });
              }}
              disabled={!form.bank_id}
              required
            >
              <option value="">Select product</option>
              {products.map((p) => (
                <option key={p.product_id} value={p.product_id}>{p.name}</option>
              ))}
            </select>
          </label>
          <label>
            Requested amount
            <input
              data-testid="application-amount-input"
              type="number"
              value={form.requested_amount}
              onChange={(e) => setForm({ ...form, requested_amount: e.target.value })}
              required
            />
          </label>
          <label>
            Application / reference number
            <input
              data-testid="application-number-input"
              value={form.application_number}
              onChange={(e) => setForm({ ...form, application_number: e.target.value })}
            />
          </label>
          <label>
            ROI %
            <input
              data-testid="application-roi-input"
              type="number"
              step="0.01"
              min="0"
              value={form.roi}
              placeholder="e.g. 11.5"
              onChange={(e) => setForm({ ...form, roi: e.target.value })}
            />
          </label>
          <label>
            Tenure (months)
            <input
              data-testid="application-tenure-input"
              type="number"
              min="0"
              value={form.tenure_months}
              placeholder="e.g. 60"
              onChange={(e) => setForm({ ...form, tenure_months: e.target.value })}
            />
          </label>
          <label>
            Commission %
            <input
              data-testid="application-commission-percent-input"
              type="number"
              step="0.01"
              min="0"
              value={form.commission_percent}
              placeholder="e.g. 1.5"
              onChange={(e) => setForm({ ...form, commission_percent: e.target.value, commission_amount: "" })}
            />
          </label>
          <label>
            Commission amount (₹)
            <input
              data-testid="application-commission-amount-input"
              type="number"
              min="0"
              value={form.commission_amount}
              placeholder="Or enter a flat amount"
              onChange={(e) => setForm({ ...form, commission_amount: e.target.value, commission_percent: "" })}
            />
          </label>
        </div>
        <p className="upload-note">
          Requested {money(form.requested_amount)} · Estimated payout {money(previewCommission)}.
          Payout is auto-invoiced when this application is marked Disbursed.
        </p>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button data-testid="application-form-submit-button" className="primary-button" disabled={busy}>
            {busy ? "Creating…" : "Create application"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- FOLLOW-UP ---------------- */
export function FollowupForm({ leadId, onClose, onSaved }) {
  const [form, setForm] = useState({ due_at: "", followup_type: "Call", notes: "", outcome: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post(`/leads/${leadId}/followups`, form);
      notify.success("Follow-up scheduled");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not schedule follow-up");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Schedule follow-up" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          Follow-up date and time
          <input
            data-testid="follow-up-date-input"
            type="datetime-local"
            value={form.due_at}
            onChange={(e) => setForm({ ...form, due_at: e.target.value })}
            required
          />
        </label>
        <label>
          Type
          <select
            data-testid="follow-up-type-input"
            value={form.followup_type}
            onChange={(e) => setForm({ ...form, followup_type: e.target.value })}
          >
            <option>Call</option>
            <option>Meeting</option>
            <option>WhatsApp</option>
            <option>Email</option>
            <option>Site visit</option>
          </select>
        </label>
        <label>
          Notes
          <textarea
            data-testid="follow-up-notes-input"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        <label>
          Outcome (optional)
          <input
            data-testid="follow-up-outcome-input"
            value={form.outcome}
            onChange={(e) => setForm({ ...form, outcome: e.target.value })}
            placeholder="e.g. Requires callback"
          />
        </label>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button data-testid="follow-up-form-submit-button" className="primary-button" disabled={busy}>
            {busy ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- ACTIVITY ---------------- */
export function ActivityForm({ leadId, onClose, onSaved }) {
  const [form, setForm] = useState({ action: "Note added", notes: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post(`/leads/${leadId}/activities`, form);
      notify.success("Activity added");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not add activity");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Add activity note" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          Activity type
          <select value={form.action} onChange={(e) => setForm({ ...form, action: e.target.value })}>
            <option>Note added</option>
            <option>Call completed</option>
            <option>Customer contacted</option>
            <option>Document received</option>
            <option>Query resolved</option>
          </select>
        </label>
        <label>
          Notes
          <textarea
            data-testid="activity-notes-input"
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            required
          />
        </label>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button data-testid="activity-form-submit-button" className="primary-button" disabled={busy}>
            {busy ? "Adding…" : "Add activity"}
          </button>
        </div>
      </form>
    </Modal>
  );
}

/* ---------------- DOCUMENT ---------------- */
export function DocumentForm({ leadId, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [type, setType] = useState("PAN");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    setBusy(true);
    try {
      const body = new FormData();
      body.append("lead_id", leadId);
      body.append("document_type", type);
      body.append("file", file);
      await http.post("/documents", body, { headers: { "Content-Type": "multipart/form-data" } });
      notify.success("Document uploaded");
      onSaved();
    } catch (err) {
      notify.error(err, "Could not upload document");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Upload customer document" onClose={onClose}>
      <form className="record-form" onSubmit={submit}>
        <label>
          Document type
          <select
            data-testid="document-type-input"
            value={type}
            onChange={(e) => setType(e.target.value)}
          >
            {documentTypes.map((x) => <option key={x}>{x}</option>)}
          </select>
        </label>
        <label>
          File
          <input
            data-testid="document-file-input"
            type="file"
            onChange={(e) => setFile(e.target.files[0])}
            required
          />
        </label>
        <p className="upload-note">
          Files are visible only within this permitted lead scope. Max 25 MB.
        </p>
        <div className="form-actions">
          <button type="button" className="outline-button" onClick={onClose}>Cancel</button>
          <button
            data-testid="document-form-submit-button"
            className="primary-button"
            disabled={!file || busy}
          >
            {busy ? "Uploading…" : "Upload file"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
